import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthFetch } from "../hooks/useAuthFetch";
import ConfirmModal from "../utils/ConfirmModal";
import "../stili/AggiornaAcquistiMateriale.css";

const API_URL = process.env.REACT_APP_API_URL;

const statoPagamentoOptions = [
  { value: "", label: "Tutti gli stati pagamento" },
  { value: "NON_PAGATO", label: "Non pagato" },
  { value: "PARZIALMENTE_PAGATO", label: "Parzialmente pagato" },
  { value: "PAGATO", label: "Pagato" },
  { value: "RIMBORSATO", label: "Rimborsato" }
];

const metodoPagamentoOptions = [
  { value: "CONTANTI", label: "Contanti" },
  { value: "BONIFICO", label: "Bonifico" },
  { value: "POS", label: "POS" },
  { value: "ALTRO", label: "Altro" }
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 14,
    background: "rgba(255,255,255,0.12)",
    borderColor: state.isFocused ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.18)",
    boxShadow: "none"
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 14,
    overflow: "hidden",
    background: "rgba(18, 31, 70, 0.96)",
    zIndex: 9999
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  }),
  singleValue: (base) => ({
    ...base,
    color: "#fff"
  }),
  input: (base) => ({
    ...base,
    color: "#fff"
  }),
  placeholder: (base) => ({
    ...base,
    color: "rgba(255,255,255,0.70)"
  }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused ? "rgba(255,255,255,0.14)" : "transparent",
    color: "#fff",
    cursor: "pointer"
  })
};

const selectPortalProps = {
  menuPortalTarget: document.body,
  menuPosition: "fixed"
};

const formatEuro = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatDateIt = (value) => {
  if (!value) return "-";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-");
    return `${d}/${m}/${y}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("it-IT");
};

const statoPagamentoLabel = (value) => {
  switch (value) {
    case "NON_PAGATO":
      return "Non pagato";
    case "PARZIALMENTE_PAGATO":
      return "Parzialmente pagato";
    case "PAGATO":
      return "Pagato";
    case "RIMBORSATO":
      return "Rimborsato";
    default:
      return value || "-";
  }
};

const statoAcquistoLabel = (value) => {
  switch (value) {
    case "ATTIVO":
      return "Attivo";
    case "ANNULLATO":
      return "Annullato";
    default:
      return value || "-";
  }
};

export default function AggiornaAcquistiMateriale() {
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  const [acquisti, setAcquisti] = useState([]);
  const [stagioni, setStagioni] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filtroTesto, setFiltroTesto] = useState("");
  const [filtroStagione, setFiltroStagione] = useState("");
  const [filtroStatoPagamento, setFiltroStatoPagamento] = useState("");
  const [soloAttivi, setSoloAttivi] = useState(true);

  const [acquistoEspanso, setAcquistoEspanso] = useState(null);

  const [modalPagamentoOpen, setModalPagamentoOpen] = useState(false);
  const [acquistoPagamento, setAcquistoPagamento] = useState(null);
  const [pagamentoForm, setPagamentoForm] = useState({
    importo: "",
    dataPagamento: "",
    metodoPagamento: null,
    note: ""
  });
  const [savingPagamento, setSavingPagamento] = useState(false);

  const [modalAnnullaOpen, setModalAnnullaOpen] = useState(false);
  const [acquistoDaAnnullare, setAcquistoDaAnnullare] = useState(null);
  const [notaAnnullamento, setNotaAnnullamento] = useState("");
  const [savingAnnullamento, setSavingAnnullamento] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [resAcquisti, resStagioni] = await Promise.all([
        authFetch(`${API_URL}/api/admin/acquisti-materiale`),
        authFetch(`${API_URL}/api/admin/lookup/stagioni`)
      ]);

      if (!resAcquisti.ok) {
        throw new Error("Errore nel caricamento acquisti materiale");
      }
      if (!resStagioni.ok) {
        throw new Error("Errore nel caricamento stagioni");
      }

      const dataAcquisti = await resAcquisti.json().catch(() => []);
      const dataStagioni = await resStagioni.json().catch(() => []);

      setAcquisti(Array.isArray(dataAcquisti) ? dataAcquisti : []);
      setStagioni(Array.isArray(dataStagioni) ? dataStagioni : []);
    } catch (err) {
      toast.error(err.message || "Errore nel caricamento dati");
      setAcquisti([]);
      setStagioni([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stagioneOptions = useMemo(() => {
    const opts = [{ value: "", label: "Tutte le stagioni" }];
    const mapped = stagioni.map((s) => ({
      value: s.id,
      label: s.nome || s.descrizione || s.label || `Stagione ${s.id}`
    }));
    return [...opts, ...mapped];
  }, [stagioni]);

  const acquistiFiltrati = useMemo(() => {
    const testo = filtroTesto.trim().toLowerCase();

    return acquisti.filter((acq) => {
      const matchTesto =
        !testo ||
        (acq.nominativoGiocatore || "").toLowerCase().includes(testo) ||
        (acq.nominativoGenitore || "").toLowerCase().includes(testo) ||
        String(acq.id || "").includes(testo);

      const matchStagione =
        !filtroStagione || String(acq.idStagione) === String(filtroStagione);

      const matchPagamento =
        !filtroStatoPagamento || acq.statoPagamento === filtroStatoPagamento;

      const matchStato = !soloAttivi || acq.stato === "ATTIVO";

      return matchTesto && matchStagione && matchPagamento && matchStato;
    });
  }, [acquisti, filtroTesto, filtroStagione, filtroStatoPagamento, soloAttivi]);

  const statistiche = useMemo(() => {
    const totaleDovuto = acquistiFiltrati.reduce(
      (acc, a) => acc + Number(a.totaleDovuto || 0),
      0
    );
    const totalePagato = acquistiFiltrati.reduce(
      (acc, a) => acc + Number(a.totalePagato || 0),
      0
    );
    const totaleResiduo = acquistiFiltrati.reduce(
      (acc, a) => acc + Number(a.residuo || 0),
      0
    );
    const aperti = acquistiFiltrati.filter((a) => a.stato === "ATTIVO").length;

    return { totaleDovuto, totalePagato, totaleResiduo, aperti };
  }, [acquistiFiltrati]);

  const toggleEspanso = (id) => {
    setAcquistoEspanso((prev) => (prev === id ? null : id));
  };

  const apriModalPagamento = (acquisto) => {
    setAcquistoPagamento(acquisto);
    setPagamentoForm({
      importo: acquisto?.residuo ? String(acquisto.residuo) : "",
      dataPagamento: "",
      metodoPagamento: null,
      note: ""
    });
    setModalPagamentoOpen(true);
  };

  const chiudiModalPagamento = () => {
    setModalPagamentoOpen(false);
    setAcquistoPagamento(null);
    setPagamentoForm({
      importo: "",
      dataPagamento: "",
      metodoPagamento: null,
      note: ""
    });
  };

  const salvaPagamento = async () => {
    if (!acquistoPagamento) return;

    const importo = Number(String(pagamentoForm.importo || "").replace(",", "."));
    if (!importo || importo <= 0) {
      toast.error("Inserisci un importo valido");
      return;
    }

    if (importo > Number(acquistoPagamento.residuo || 0)) {
      toast.error("L'importo supera il residuo dell'acquisto");
      return;
    }

    if (!pagamentoForm.metodoPagamento) {
      toast.error("Seleziona il metodo di pagamento");
      return;
    }

    setSavingPagamento(true);
    try {
      const payload = {
        importo,
        dataPagamento: pagamentoForm.dataPagamento || null,
        metodoPagamento: pagamentoForm.metodoPagamento.value,
        note: pagamentoForm.note?.trim() || null
      };

      const res = await authFetch(
        `${API_URL}/api/admin/acquisti-materiale/${acquistoPagamento.id}/pagamenti`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Errore durante la registrazione del pagamento");
      }

      toast.success("Pagamento registrato correttamente");
      chiudiModalPagamento();
      await fetchData();
    } catch (err) {
      toast.error(err.message || "Errore durante il pagamento");
    } finally {
      setSavingPagamento(false);
    }
  };

  const apriModalAnnullamento = (acquisto) => {
    setAcquistoDaAnnullare(acquisto);
    setNotaAnnullamento("");
    setModalAnnullaOpen(true);
  };

  const chiudiModalAnnullamento = () => {
    setModalAnnullaOpen(false);
    setAcquistoDaAnnullare(null);
    setNotaAnnullamento("");
  };

  const confermaAnnullamento = async () => {
    if (!acquistoDaAnnullare) return;

    setSavingAnnullamento(true);
    try {
      const query = notaAnnullamento.trim()
        ? `?nota=${encodeURIComponent(notaAnnullamento.trim())}`
        : "";

      const res = await authFetch(
        `${API_URL}/api/admin/acquisti-materiale/${acquistoDaAnnullare.id}/annulla${query}`,
        {
          method: "POST"
        }
      );

      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || "Errore durante l'annullamento");
      }

      toast.success("Acquisto annullato correttamente. Ripristino magazzino e storno cassa eseguiti se dovuti.");
      chiudiModalAnnullamento();
      await fetchData();
    } catch (err) {
      toast.error(err.message || "Errore durante l'annullamento");
    } finally {
      setSavingAnnullamento(false);
    }
  };

  return (
    <div className="sfondo-immagine">
      <div className="aggiorna-acquisti-page">
        <div className="aggiorna-acquisti-container">
          <div className="aggiorna-acquisti-header card-glass">
            <div className="aggiorna-acquisti-header-top">
              <div>
                <h2 className="aggiorna-acquisti-title">Gestione Acquisti Materiale</h2>
                <p className="aggiorna-acquisti-subtitle">
                  Consulta gli acquisti registrati, monitora i residui e gestisci pagamenti o annullamenti.
                </p>
              </div>

              <button
                type="button"
                className="btn-secondario"
                onClick={() => navigate("/gestionale")}
              >
                Torna al gestionale
              </button>
            </div>
          </div>

          <div className="aggiorna-acquisti-stats">
            <div className="card-glass stat-card">
              <span>Totale dovuto</span>
              <strong>{formatEuro(statistiche.totaleDovuto)} €</strong>
            </div>
            <div className="card-glass stat-card">
              <span>Totale incassato</span>
              <strong>{formatEuro(statistiche.totalePagato)} €</strong>
            </div>
            <div className="card-glass stat-card stat-card-alert">
              <span>Residui</span>
              <strong>{formatEuro(statistiche.totaleResiduo)} €</strong>
            </div>
            <div className="card-glass stat-card">
              <span>Acquisti attivi</span>
              <strong>{statistiche.aperti}</strong>
            </div>
          </div>

          <div className="card-glass aggiorna-acquisti-filtri">
            <div className="aggiorna-acquisti-filtri-grid">
              <div className="aggiorna-acquisti-field">
                <label>Ricerca</label>
                <input
                  type="text"
                  value={filtroTesto}
                  onChange={(e) => setFiltroTesto(e.target.value)}
                  placeholder="Giocatore, genitore o ID acquisto"
                />
              </div>

              <div className="aggiorna-acquisti-field">
                <label>Stagione</label>
                <Select
                  {...selectPortalProps}
                  options={stagioneOptions}
                  value={stagioneOptions.find((opt) => String(opt.value) === String(filtroStagione)) || stagioneOptions[0]}
                  onChange={(opt) => setFiltroStagione(opt?.value || "")}
                  styles={selectStyles}
                  isClearable={false}
                />
              </div>

              <div className="aggiorna-acquisti-field">
                <label>Stato pagamento</label>
                <Select
                  {...selectPortalProps}
                  options={statoPagamentoOptions}
                  value={
                    statoPagamentoOptions.find((opt) => opt.value === filtroStatoPagamento) ||
                    statoPagamentoOptions[0]
                  }
                  onChange={(opt) => setFiltroStatoPagamento(opt?.value || "")}
                  styles={selectStyles}
                  isClearable={false}
                />
              </div>

              <div className="aggiorna-acquisti-check">
                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={soloAttivi}
                    onChange={(e) => setSoloAttivi(e.target.checked)}
                  />
                  Mostra solo acquisti attivi
                </label>
              </div>
            </div>
          </div>

          <div className="aggiorna-acquisti-list">
            {loading ? (
              <div className="card-glass empty-state">Caricamento acquisti materiale...</div>
            ) : acquistiFiltrati.length === 0 ? (
              <div className="card-glass empty-state">Nessun acquisto trovato con i filtri selezionati.</div>
            ) : (
              acquistiFiltrati.map((acquisto) => {
                const espanso = acquistoEspanso === acquisto.id;
                const puoPagare =
                  acquisto.stato === "ATTIVO" &&
                  Number(acquisto.residuo || 0) > 0;

                const puoAnnullare = acquisto.stato === "ATTIVO";

                return (
                  <div
                    key={acquisto.id}
                    className={`card-glass acquisto-item-card ${
                      acquisto.stato === "ANNULLATO" ? "acquisto-item-card-annullato" : ""
                    }`}
                  >
                    <div className="acquisto-item-top">
                      <div>
                        <h3>
                          Acquisto #{acquisto.id} - {acquisto.nominativoGiocatore || "-"}
                        </h3>
                        <p className="acquisto-item-meta">
                          Genitore: <strong>{acquisto.nominativoGenitore || "-"}</strong> · Stagione:{" "}
                          <strong>{acquisto.nomeStagione || "-"}</strong> · Data:{" "}
                          <strong>{formatDateIt(acquisto.dataAcquisto)}</strong>
                        </p>
                      </div>

                      <div className="acquisto-badges">
                        <span
                          className={`badge-stato-pagamento ${
                            acquisto.statoPagamento === "PAGATO"
                              ? "ok"
                              : acquisto.statoPagamento === "PARZIALMENTE_PAGATO"
                              ? "warn"
                              : acquisto.statoPagamento === "RIMBORSATO"
                              ? "off"
                              : "danger"
                          }`}
                        >
                          {statoPagamentoLabel(acquisto.statoPagamento)}
                        </span>

                        <span
                          className={`badge-stato-acquisto ${
                            acquisto.stato === "ATTIVO" ? "attivo" : "annullato"
                          }`}
                        >
                          {statoAcquistoLabel(acquisto.stato)}
                        </span>
                      </div>
                    </div>

                    <div className="acquisto-item-grid">
                      <div>
                        <span>Totale dovuto</span>
                        <strong>{formatEuro(acquisto.totaleDovuto)} €</strong>
                      </div>
                      <div>
                        <span>Totale pagato</span>
                        <strong>{formatEuro(acquisto.totalePagato)} €</strong>
                      </div>
                      <div>
                        <span>Residuo</span>
                        <strong>{formatEuro(acquisto.residuo)} €</strong>
                      </div>
                      <div>
                        <span>Ultimo pagamento</span>
                        <strong>
                          {acquisto.metodoUltimoPagamento
                            ? `${acquisto.metodoUltimoPagamento} - ${formatDateIt(acquisto.dataUltimoPagamento)}`
                            : "-"}
                        </strong>
                      </div>
                    </div>

                    {acquisto.note && (
                      <div className="acquisto-note-box">
                        <p>
                          <strong>Note:</strong> {acquisto.note}
                        </p>
                      </div>
                    )}

                    <div className="acquisto-item-actions">
                      <button
                        type="button"
                        className="btn-secondario"
                        onClick={() => toggleEspanso(acquisto.id)}
                      >
                        {espanso ? "Nascondi dettagli" : "Mostra dettagli"}
                      </button>

                      <button
                        type="button"
                        className="btn-secondario"
                        onClick={() => apriModalPagamento(acquisto)}
                        disabled={!puoPagare}
                      >
                        Registra pagamento
                      </button>

                      <button
                        type="button"
                        className="btn-secondario btn-danger-soft"
                        onClick={() => apriModalAnnullamento(acquisto)}
                        disabled={!puoAnnullare}
                      >
                        Annulla acquisto
                      </button>
                    </div>

                    {espanso && (
                      <div className="dettaglio-righe-box">
                        <h4>Dettaglio articoli</h4>

                        {!Array.isArray(acquisto.righe) || acquisto.righe.length === 0 ? (
                          <div className="empty-substate">Nessuna riga disponibile.</div>
                        ) : (
                          <div className="dettaglio-righe-list">
                            {acquisto.righe.map((riga) => (
                              <div key={riga.id} className="riga-dettaglio-card">
                                <div>
                                  <span>Articolo</span>
                                  <strong>
                                    {riga.descrizioneArticolo || "-"}{" "}
                                    {riga.taglia ? `- Taglia ${riga.taglia}` : ""}
                                  </strong>
                                </div>
                                <div>
                                  <span>Codice</span>
                                  <strong>{riga.codiceArticolo || "-"}</strong>
                                </div>
                                <div>
                                  <span>Quantità</span>
                                  <strong>{riga.quantita}</strong>
                                </div>
                                <div>
                                  <span>Prezzo unitario</span>
                                  <strong>{formatEuro(riga.prezzoUnitario)} €</strong>
                                </div>
                                <div>
                                  <span>Totale riga</span>
                                  <strong>{formatEuro(riga.totaleRiga)} €</strong>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={modalPagamentoOpen}
        title="Registra pagamento acquisto materiale"
        message={
          acquistoPagamento ? (
            <div className="modal-pagamento-body">
              <p>
                Acquisto: <strong>#{acquistoPagamento.id}</strong>
              </p>
              <p>
                Giocatore: <strong>{acquistoPagamento.nominativoGiocatore}</strong>
              </p>
              <p>
                Residuo attuale: <strong>{formatEuro(acquistoPagamento.residuo)} €</strong>
              </p>

              <div className="aggiorna-acquisti-field">
                <label>Importo</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={pagamentoForm.importo}
                  onChange={(e) =>
                    setPagamentoForm((prev) => ({ ...prev, importo: e.target.value }))
                  }
                />
              </div>

              <div className="aggiorna-acquisti-field">
                <label>Data pagamento</label>
                <input
                  type="date"
                  value={pagamentoForm.dataPagamento}
                  onChange={(e) =>
                    setPagamentoForm((prev) => ({ ...prev, dataPagamento: e.target.value }))
                  }
                />
              </div>

              <div className="aggiorna-acquisti-field">
                <label>Metodo pagamento</label>
                <Select
                  {...selectPortalProps}
                  options={metodoPagamentoOptions}
                  value={pagamentoForm.metodoPagamento}
                  onChange={(opt) =>
                    setPagamentoForm((prev) => ({ ...prev, metodoPagamento: opt || null }))
                  }
                  styles={selectStyles}
                  placeholder="Seleziona metodo"
                  isClearable
                />
              </div>

              <div className="aggiorna-acquisti-field">
                <label>Note</label>
                <input
                  type="text"
                  value={pagamentoForm.note}
                  onChange={(e) =>
                    setPagamentoForm((prev) => ({ ...prev, note: e.target.value }))
                  }
                  placeholder="Note pagamento"
                />
              </div>
            </div>
          ) : null
        }
        confirmText={savingPagamento ? "Salvataggio..." : "Conferma pagamento"}
        cancelText="Chiudi"
        onConfirm={salvaPagamento}
        onClose={chiudiModalPagamento}
      />

      <ConfirmModal
        isOpen={modalAnnullaOpen}
        title="Annulla acquisto materiale"
        message={
          acquistoDaAnnullare ? (
            <div className="modal-pagamento-body">
              <p>
                Stai per annullare l'acquisto <strong>#{acquistoDaAnnullare.id}</strong> di{" "}
                <strong>{acquistoDaAnnullare.nominativoGiocatore}</strong>.
              </p>
              <p>
                Lo stock verrà ripristinato automaticamente.
              </p>
              <p>
                Se l'acquisto risulta già pagato, verrà registrato anche lo storno in cassa.
              </p>

              <div className="aggiorna-acquisti-field">
                <label>Nota annullamento</label>
                <input
                  type="text"
                  value={notaAnnullamento}
                  onChange={(e) => setNotaAnnullamento(e.target.value)}
                  placeholder="Motivo annullamento"
                />
              </div>
            </div>
          ) : null
        }
        confirmText={savingAnnullamento ? "Annullamento..." : "Conferma annullamento"}
        cancelText="Chiudi"
        onConfirm={confermaAnnullamento}
        onClose={chiudiModalAnnullamento}
      />
    </div>
  );
}